import React from 'react';
import { Heart, Target, Eye, Star } from 'lucide-react';

const AboutPage: React.FC = () => {
  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              Quem Somos
            </h1>
            <p className="text-xl leading-relaxed text-orange-100">
              A Fox Vendas nasceu da paixão por conectar pessoas aos seus lares dos sonhos, 
              sempre com transparência, dedicação e excelência no atendimento.
            </p>
          </div>
        </div>
      </section>

      {/* Nossa História - Primeira Linha */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                  <Heart className="w-6 h-6 text-orange-500" />
                </div>
                <h2 className="text-3xl font-bold text-gray-800">Nossa História</h2>
              </div>
              <div className="text-gray-600 leading-relaxed space-y-4">
                <p>
                  A Fox nasceu em outubro de 2021, impulsionada pelo espírito empreendedor de seu fundador, 
                  ex-gerente da incorporadora Tegra, com o objetivo de transformar o mercado imobiliário por 
                  meio de inovação e tecnologia. A empresa começou pequena, com apenas três corretores, mas 
                  com uma visão ambiciosa: criar uma imobiliária moderna, ágil e orientada por resultados.
                </p>
                <p>
                  O crescimento foi rápido. Em maio de 2022, foi contratado o primeiro gerente, e em agosto, 
                  a equipe já demandava um novo escritório. Em 2023, Guilherme tornou-se sócio e liderou a 
                  abertura da unidade em Paulínia, também contribuindo com a operação em Campinas.
                </p>
                <p>
                  Em 2024, com a nova unidade e um plano de negócios reformulado, a Fox iniciou sua expansão 
                  estratégica baseada em dados, tecnologia e inteligência. A meta é abrir seis unidades 
                  segmentadas e um escritório central que integre gestão, marketing, CRM e dados.
                </p>
                <p>
                  Hoje, a Fox conta com mais de 30 colaboradores e segue crescendo com uma cultura forte, 
                  gestão estratégica e um mindset digital. Mais que uma imobiliária, a Fox se posiciona 
                  como uma plataforma de crescimento e inovação no setor.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Víctor Nardy - Segunda Linha */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Foto à esquerda */}
                <div className="relative overflow-hidden aspect-square">
                  <img 
                    src="/lovable-uploads/ab97d26d-c5e0-48d5-8a17-7c6c477eeb7c.png" 
                    alt="Víctor Nardy - Sócio Fundador"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Víctor Nardy</h3>
                    <p className="text-orange-300 font-semibold">Sócio Fundador</p>
                  </div>
                </div>
                
                {/* História à direita */}
                <div className="p-8 flex flex-col justify-center">
                  <h4 className="text-2xl font-bold text-gray-800 mb-4">Sobre Víctor</h4>
                  <div className="text-gray-600 leading-relaxed space-y-4">
                    <p>
                      Com uma trajetória de 18 anos como empresário em diferentes segmentos, Victor Nardy construiu sua carreira pautada na visão estratégica e na busca constante por soluções que realmente façam diferença para clientes e parceiros.
                    </p>
                    <p>
                      Nos últimos anos, encontrou no mercado imobiliário um espaço para unir experiência em negócios e paixão por ajudar pessoas a conquistarem seus objetivos. Como fundador da Fox Vendas, Victor atua com foco em lançamentos imobiliários, liderando uma equipe experiente e dedicada a entregar um atendimento transparente e seguro em todas as etapas da compra de um imóvel.
                    </p>
                    <p>
                      Além da atuação direta com vendas, é também o criador do Rocket Lead, um sistema de CRM pensado para atender as reais necessidades de corretores e imobiliárias, trazendo mais organização e eficiência para o setor.
                    </p>
                    <p>
                      Com uma postura tranquila, analítica e voltada para resultados sustentáveis, Victor acredita que construir relações sólidas é o caminho para transformar o mercado imobiliário e gerar valor para clientes, equipes e parceiros.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guilherme Calvi - Terceira Linha */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* História à esquerda */}
                <div className="p-8 flex flex-col justify-center">
                  <h4 className="text-2xl font-bold text-gray-800 mb-4">Sobre Guilherme</h4>
                  <div className="text-gray-600 leading-relaxed space-y-4">
                    <p>
                      Guilherme Calvi é o Sócio Diretor da Fox Vendas, responsável pela operação 
                      e gestão estratégica da empresa. Com vasta experiência em vendas e 
                      desenvolvimento de negócios, ele é peça fundamental no crescimento da empresa.
                    </p>
                    <p>
                      Formado em Engenharia e com especialização em Gestão Empresarial, 
                      Guilherme traz uma visão analítica e estratégica para os negócios. 
                      Sua expertise em processos e tecnologia tem sido crucial para a 
                      modernização dos serviços da Fox Vendas.
                    </p>
                    <p>
                      Conhecido por sua dedicação e comprometimento com a excelência, 
                      Guilherme lidera a equipe com foco em resultados e satisfação do cliente. 
                      Sua paixão por inovação tem impulsionado a empresa a adotar novas 
                      tecnologias e metodologias.
                    </p>
                  </div>
                </div>

                {/* Foto à direita */}
                <div className="relative overflow-hidden">
                  <img 
                    src="/lovable-uploads/f656cec9-7331-46d3-8209-63265f09ae82.png" 
                    alt="Guilherme Calvi - Sócio Diretor"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Guilherme Calvi</h3>
                    <p className="text-orange-300 font-semibold">Sócio Diretor</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Missão, Visão e Valores */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-8">
            {/* Primeira linha: Missão e Visão */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Missão */}
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                    <Target className="w-6 h-6 text-orange-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">Missão</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Conectar pessoas ao imóvel ideal, com uma equipe comprometida e preparada para oferecer 
                  soluções ágeis e resultados concretos, proporcionando uma experiência única para nossos clientes.
                </p>
              </div>

              {/* Visão */}
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                    <Eye className="w-6 h-6 text-orange-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">Visão</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Ser referência regional em soluções imobiliárias inovadoras e de alto padrão, reconhecida 
                  pela performance da equipe e impacto positivo na vida dos nossos clientes e colaboradores.
                </p>
              </div>
            </div>

            {/* Segunda linha: Valores */}
            <div className="grid grid-cols-1">
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                    <Star className="w-6 h-6 text-orange-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">Valores</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Acreditamos em uma cultura baseada na atitude do dono, onde cada um se compromete com o crescimento 
                  e cuida da imagem, equipe e clientes. Valorizamos a proatividade, sempre agindo com iniciativa para 
                  criar novas oportunidades e alcançar resultados. Trabalhamos com autonomia, mas com responsabilidade 
                  e credibilidade, conduzindo nossas ações com clareza e foco nos resultados. Buscamos o crescimento 
                  contínuo e inovação, aprendendo constantemente e adotando novas estratégias e tecnologias. Oferecemos 
                  um atendimento com excelência, tratando cada cliente de forma única, com soluções personalizadas e 
                  cuidadosas. E acima de tudo, cultivamos o trabalho em equipe, com relações pautadas na confiança, 
                  respeito e valorização de cada conquista, seja individual ou coletiva.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutPage;